import { Check, Copy } from "lucide-react";
import React, { useState } from "react";
import { Button } from "src/components/ui/button";

// eslint-disable-next-line custom/no-hardcoded-brand-name -- GCP service account
const TIERZERO_SERVICE_ACCOUNT = import.meta.env.VITE_GCP_SA_EMAIL || "tierzero-managed@tierzero-abcfa.iam.gserviceaccount.com";

interface GcpCloudMonitoringInstructionsProps {
  productName?: string;
}

const GcpCloudMonitoringInstructions: React.FC<GcpCloudMonitoringInstructionsProps> = ({ productName = "TierZero" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">Create a Service Account:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into{" "}
            <a
              href="https://console.cloud.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline"
            >
              console.cloud.google.com
            </a>
          </li>
          <li>
            Navigate to{" "}
            <span className="text-amber-600">IAM & Admin</span>
            {" > "}
            <span className="text-amber-600">Service Accounts</span> in the main menu.
          </li>
          <li>
            Click <span className="text-amber-600">Create Service Account</span>.
          </li>
          <li>
            Enter a name for your service account (e.g.,{" "}
            {/* eslint-disable-next-line custom/no-hardcoded-brand-name -- GCP setup instructions */}
            <span className="font-mono text-green-700 dark:text-green-400">tierzero-gcp-access</span>) and click{" "}
            <span className="text-amber-600">Create and Continue</span>.
          </li>
          <li>
            In the "Grant this service account access to project" step, add the following roles:
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-muted-foreground">
              <li><span className="font-mono text-green-700 dark:text-green-400">Browser</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">Monitoring Viewer</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">Logs Viewer</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">Private Logs Viewer</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">Cloud Trace User</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">Error Reporting Viewer</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">Observability Viewer</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">BigQuery User</span></li>
              <li><span className="font-mono text-green-700 dark:text-green-400">BigQuery Data Viewer</span></li>
            </ul>
          </li>
          <li>
            Click <span className="text-amber-600">Continue</span>, then{" "}
            <span className="text-amber-600">Done</span>.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Grant {productName} Access:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Go to{" "}
            <span className="text-amber-600">IAM & Admin</span>
            {" > "}
            <span className="text-amber-600">Service Accounts</span>.
          </li>
          <li>
            Click on the service account you just created (e.g.,{" "}
            <span className="font-mono text-green-700 dark:text-green-400">&lt;service-account-name&gt;@&lt;project-name&gt;.iam.gserviceaccount.com</span>).
          </li>
          <li>
            Select the <span className="text-amber-600">Principals with access</span> tab, then click{" "}
            <span className="text-amber-600">Grant Access</span>.
          </li>
          <li>
            In the "New principals" field, enter:
            <div className="inline-flex items-center bg-accent border border-border rounded-md px-3 py-1 font-mono text-xs text-foreground ml-1">
              <span className="select-all">
                {TIERZERO_SERVICE_ACCOUNT}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-2 h-4 w-4 p-0 hover:bg-muted"
                onClick={() => handleCopy(TIERZERO_SERVICE_ACCOUNT)}
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="h-3 w-3 shrink-0 text-green-400" />
                ) : (
                  <Copy className="h-3 w-3 shrink-0" />
                )}
              </Button>
            </div>
          </li>
          <li>
            Assign the role <span className="font-mono text-green-700 dark:text-green-400">Service Account Token Creator</span>.
          </li>
          <li>
            Click <span className="text-amber-600">Save</span>.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Enter Your Details Below:</p>
        <ul className="text-sm space-y-2 list-disc list-inside">
          <li>
            <span className="font-semibold text-foreground">Service Account Email:</span>{" "}
            The email of the service account you created (e.g.,{" "}
            <span className="font-mono text-green-700 dark:text-green-400">&lt;service-account-name&gt;@&lt;project-name&gt;.iam.gserviceaccount.com</span>)
          </li>
        </ul>
      </div>
    </div>
  );
};

export default GcpCloudMonitoringInstructions;
